
import { Component, Inject, LOCALE_ID, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Digest } from './digest.type';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '@teammood/shared/util/logger';
import { DigestsService } from './digests.service';
import { SnackBarService } from '@teammood/shared/ui/snack-bar';
import { FilterCodec } from '@teammood/filter';
import { FrequencyService } from './frequency.service';
import { DigestDialogService } from './digest-dialog/digest-dialog.service';
import { takeUntil, tap } from 'rxjs';

@Component({
    selector: 'digests',
    templateUrl: './digests.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DigestsComponent implements OnInit {
    daysOfWeek: Array<{value: number, label: string}>;
    frequencies: Array<{value: string, label: string}>;
    digests: Array<Digest>;
    newDigest: Digest;
    show: boolean;
    updateMode: boolean;
    locale: string;

    constructor(
        @Inject(LOCALE_ID) locale: string,
        private translate: TranslateService,
        private logger: LoggerService,
        private digestsService: DigestsService,
        private snackBarService: SnackBarService,
        private filterCodec: FilterCodec,
        private frequencyService: FrequencyService,
        private digestDialogService: DigestDialogService,
    ) {
        this.locale = locale;
    }

    async ngOnInit() {
        await this.load();
        this.daysOfWeek = this.frequencyService.getWeekDays();
        this.frequencies = this.frequencyService.getFrequency();
    }

    async createOrUpdate(data : Digest) {
        try {
            if (data.dayOfDigest && data.frequency && data.decodedFilter && data.decodedFilter.predicates && data.decodedFilter.predicates.length > 0) {
                await this.digestsService.createOrUpdate(data);
                await this.load();
            }
        } catch (error) {
            this.logger.error("digests create or update error", data, error);
        }
    }

    async test(id: string) {
        try {
            await this.digestsService.test(id);
            this.snackBarService.success(this.translate.instant('digests_setting.test.success'));
        } catch (error) {
            this.snackBarService.error(this.translate.instant('digests_setting.test.error'));
            this.logger.error("digest" + id + " test error", error);
        }
    }


    async remove(id: string) {
        try {
            await this.digestsService.remove(id);
            await this.load();
        } catch (error) {
            this.logger.error("digests" + id + " remove error", error);
        }
    }

    private async load() {
        try {
            const result : Array<Digest> = await this.digestsService.digests();

            this.digests = result.map(digest => {
                return {
                    ...digest,
                    decodedFilter: this.filterCodec.decode(digest.filter),
                }
            }).reverse();
        } catch (error) {
            this.logger.error("digests loading error", error);
            if (error.status === 403) {
                alert('Well tried, but you are not authorized or you have to upgrade to the Premium plan');
            }
        }
    }

    async onOpenDigestDialog(digest: Digest) {
        const dialogRef = await this.digestDialogService.openDigestDialog(digest);

        dialogRef.beforeClosed().pipe(
            tap(data => {
                if (data) this.createOrUpdate(data.digest);
                if (!data)this.load(); // reload initial digest on cancel
            }),
            takeUntil(dialogRef.afterClosed())
        ).subscribe();
    }

    async addDigest (){
        this.newDigest = {
            id: undefined,
            enabled: true,
            dayOfDigest: null,
            frequency: 'weekly',
            decodedFilter: {
                predicates: []
            },
        };
        await this.onOpenDigestDialog(this.newDigest);
    }

    async openUpdateModale (digestToUpdate: Digest) {
        this.newDigest = digestToUpdate;
        await this.onOpenDigestDialog(this.newDigest);
        this.updateMode = true;
    }

    async duplicate (digest: Digest) {
        const digestCopy = {
            ...digest
        };
        digestCopy.id = undefined;
        await this.createOrUpdate(digestCopy);
        await this.load();
    }

    async toggle_status (digest: Digest) {
        digest.enabled = !digest.enabled;
        await this.createOrUpdate(digest);
    }
}
